import { useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, TouchableOpacity, type TouchableOpacityProps } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { webOnlyStyles } from '../../../../../../utils';
import { Icon } from '../../../../../Icon';
import {
  TOGGLE_ANIMATIONS,
  TOGGLE_ICON_SIZE,
  TOGGLE_ICON_SIZE_NEW,
  TOGGLE_SIZE,
  TOGGLE_SIZE_NEW,
} from '../../Floating.contants';

// ─────────────────────────────────────────────────────────────────────────────

export const Toggle = (props: Props) => {
  const { isDialogShown, style, dialogNativeId, variant = 'standard', ...restProps } = props;

  // ─── Derived Data ────────────────────────────────────────────────────

  const iconSize = variant === 'compact' ? TOGGLE_ICON_SIZE_NEW : TOGGLE_ICON_SIZE;

  // ─── Shared Values ───────────────────────────────────────────────────

  const showButtonShownSV = useSharedValue(1);
  const hideButtonShownSV = useSharedValue(0);

  // ─── Styles ──────────────────────────────────────────────────────────

  const showAnimatedStyles = useAnimatedStyle(() => ({
    opacity: showButtonShownSV.value,
  }));
  const hideButtonAnimatedStyles = useAnimatedStyle(() => ({
    opacity: hideButtonShownSV.value,
  }));

  const toggleVariantStyles = useMemo(() => {
    if (variant === 'compact') {
      return [styles.toggleCompact, isDialogShown ? undefined : styles.toggleCompactShadow];
    }

    return styles.toggleStandard;
  }, [isDialogShown, variant]);

  const toggleStyles = [webStyles, styles.toggle, toggleVariantStyles, style];

  const showContentContainerStyles = [showAnimatedStyles, styles.contentContainer]; // prettier-ignore
  const hideContentContainerStyles = [hideButtonAnimatedStyles, styles.contentContainer, styles.contentContainerClose]; // prettier-ignore

  // ─── Helpers ─────────────────────────────────────────────────────────

  const toggleAnimations = useCallback(() => {
    if (!isDialogShown) return;

    const {
      SHOW_ENTRY_DURATION,
      SHOW_EXIT_DELAY,
      SHOW_EXIT_DURATION,
      HIDE_ENTER_DELAY,
      HIDE_ENTER_DURATION,
      HIDE_EXIT_DURATION,
    } = TOGGLE_ANIMATIONS;

    showButtonShownSV.value = withTiming(0, { duration: SHOW_ENTRY_DURATION });
    hideButtonShownSV.value = withDelay(HIDE_ENTER_DELAY, withTiming(1, { duration: HIDE_ENTER_DURATION })); // prettier-ignore

    return () => {
      showButtonShownSV.value = withDelay(SHOW_EXIT_DELAY, withTiming(1, { duration: SHOW_EXIT_DURATION })); // prettier-ignore
      hideButtonShownSV.value = withTiming(0, { duration: HIDE_EXIT_DURATION }); // prettier-ignore
    };
  }, [hideButtonShownSV, isDialogShown, showButtonShownSV]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(toggleAnimations, [toggleAnimations]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      accessibilityRole="button"
      aria-expanded={isDialogShown}
      aria-haspopup="dialog"
      aria-controls={dialogNativeId}
      style={toggleStyles}
      activeOpacity={0.9}
      {...restProps}
    >
      <Animated.View style={showContentContainerStyles} pointerEvents="none">
        <Icon name="IconForkAndPlate" color={theme.colors.KALE} width={iconSize} />
      </Animated.View>

      <Animated.View style={hideContentContainerStyles} pointerEvents="none">
        <Icon name="IconClose" color={theme.colors.KALE} width={iconSize} />
      </Animated.View>
    </TouchableOpacity>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  toggle: {
    marginLeft: 'auto',
  },
  toggleStandard: {
    width: TOGGLE_SIZE,
    height: TOGGLE_SIZE,
    borderRadius: TOGGLE_SIZE,
  },
  toggleCompact: {
    width: TOGGLE_SIZE_NEW,
    height: TOGGLE_SIZE_NEW,
    borderRadius: TOGGLE_SIZE_NEW,
    backgroundColor: theme.colors.WHITE,
  },
  toggleCompactShadow: {
    boxShadow: '0px 0px 20px 0px rgba(14, 21, 14, 0.05)',
  },
  contentContainer: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: TOGGLE_SIZE,
  },
  contentContainerClose: {
    backgroundColor: theme.colors.OPACITY.KALE.LIGHTEST,
  },
});

const webStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: 2,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = Omit<TouchableOpacityProps, 'children'> & {
  isDialogShown: boolean;
  dialogNativeId: string;
  variant?: 'compact' | 'standard';
};
